import { configuredFetch } from 'api/base';
import {
  Organization,
  User,
  NotificationSubscription,
  RequestNotificationSubscription,
  GetOrganizationUserNotificationSubscriptionsResponse,
  NotificationStepDefinition,
  NotificationEvent,
  NotificationChannel,
} from 'models';

export type GetSubscriptionsRequestParams = {
  orgId: Organization['id'];
  userId: User['id'];
};

function subscriptionsUrl({ orgId, userId }: GetSubscriptionsRequestParams) {
  return `/orgs/${orgId}/users/${userId}/notifications/subscriptions`;
}

async function getSubscriptions({
  orgId,
  userId,
}: GetSubscriptionsRequestParams): Promise<GetOrganizationUserNotificationSubscriptionsResponse> {
  const url = subscriptionsUrl({ orgId, userId });

  const { data } =
    await configuredFetch<GetOrganizationUserNotificationSubscriptionsResponse>(
      url
    );
  return data;
}

export type AddSubscriptionRequestParams = GetSubscriptionsRequestParams & {
  subscription: RequestNotificationSubscription;
};
async function addSubscription({
  orgId,
  userId,
  subscription,
}: AddSubscriptionRequestParams): Promise<NotificationSubscription> {
  const url = subscriptionsUrl({ orgId, userId });

  const { data } = await configuredFetch<NotificationSubscription>(url, {
    method: 'put',
    body: JSON.stringify(subscription),
  });
  return data;
}

export type UnsubscribeRequestParams = GetSubscriptionsRequestParams & {
  eventId: NotificationEvent['id'];
  channelId?: NotificationChannel['id'];
  stepDefinitionId?: NotificationStepDefinition['id'];
};
async function unsubscribe({
  orgId,
  userId,
  eventId,
  channelId,
}: UnsubscribeRequestParams): Promise<
  Pick<UnsubscribeRequestParams, 'eventId' | 'channelId'>
> {
  const url = subscriptionsUrl({ orgId, userId });

  const { data: success } = await configuredFetch<boolean>(`${url}`, {
    method: 'delete',
    body: JSON.stringify({ eventId, channelId }),
  });

  if (!success) throw new Error('Unable to unsubscribe from notification.');

  return {
    eventId,
    channelId,
  };
}

export type StepSubscriptionRequestParams = GetSubscriptionsRequestParams & {
  eventId: NotificationEvent['id'];
  stepDefinitionId: NotificationStepDefinition['id'];
  subscribed: boolean;
};
async function setStepSubscription({
  orgId,
  userId,
  eventId,
  stepDefinitionId,
  subscribed,
}: StepSubscriptionRequestParams): Promise<
  Pick<
    StepSubscriptionRequestParams,
    'eventId' | 'stepDefinitionId' | 'subscribed'
  >
> {
  const url = subscriptionsUrl({ orgId, userId });

  const { data: success } = await configuredFetch<boolean>(`${url}`, {
    method: subscribed ? 'put' : 'delete',
    body: JSON.stringify({ eventId, stepDefinitionId }),
  });

  if (!success) throw new Error('Unable to disable notifications for step.');

  return {
    eventId,
    stepDefinitionId,
    subscribed,
  };
}

export default {
  getSubscriptions,
  addSubscription,
  unsubscribe,
  setStepSubscription,
};
